import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { constants } from '../../../constants';
import { countries, countriesMap } from '../../../countries';
import { continents } from '../../../regions';
import { AgeGroup } from '../../main/models/age-group.model';
import { ToggleableNameValuePair } from '../../main/models/name-value-pair.model';
import { Region } from '../../main/models/region-model';
import { SelectorCountry } from '../../main/models/selector-country.model';
import { SpendingGroup } from '../../main/models/spending-group.model';
import { SystemGroupsService } from '../../main/services/system-groups.service';
import { AuthService } from './auth.service';
import { IncomeGroup } from '../../main/models/income-group.model';
import { Permissions } from '../models/permissions.model';

@Injectable({ providedIn: 'root' })
export class AuthorizedResourcesService implements OnDestroy {
  private authRegions?: Region[];
  private authCountries?: SelectorCountry[];
  private authCountriesMap?: Map<string, SelectorCountry>;
  private authAgeGroups?: AgeGroup[];
  private authGenerations?: ToggleableNameValuePair[];
  private authGenders?: ToggleableNameValuePair[];
  private authSpendingGroups?: SpendingGroup[];
  private authIncomeGroups?: IncomeGroup[];
  private ngUnsubscribe: Subject<void> = new Subject();
  private hasCategoriesEnabled: boolean;
  private permissions: Permissions;
  public areResourcesLoading = false;

  constructor(
    private authService: AuthService,
    private systemGroupsService: SystemGroupsService
  ) {
    this.authService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.resetResources();
      });
    this.authService.categoriesEnabled$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isEnabled) => (this.hasCategoriesEnabled = isEnabled));
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((permissions) => (this.permissions = permissions));
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public resetResources = () => {
    this.areResourcesLoading = true;
    this.systemGroupsService
      .getSystemGroupsRequest()
      .pipe(take(1))
      .subscribe(() => {
        this.authRegions = undefined;
        this.authCountries = undefined;
        this.authCountriesMap = undefined;
        this.authAgeGroups = undefined;
        this.authGenders = undefined;
        this.authSpendingGroups = undefined;
        this.authIncomeGroups = undefined;
        this.authGenerations = undefined;
        this.areResourcesLoading = false;
      });
  };

  public get regions(): Region[] {
    if (!this.authRegions) {
      this.authRegions = continents.map((region: Region) => {
        const copy = { ...region };
        copy.countries = copy.countries.filter((code) => {
          if (countriesMap.has(code) && !this.authService.isLoggedIn()) {
            return true;
          }

          return false;
        });

        return copy;
      });
    }

    return this.authRegions;
  }

  public get countries(): SelectorCountry[] {
    if (!this.authCountries) {
      this.authCountries = countries.map((c: SelectorCountry) => {
        const countryAuthDisabled = this.permissions.allowedCountries
          ?.permissionExtended?.length
          ? !this.permissions.allowedCountries?.permissionExtended?.some(
              (countries) => countries === c.code
            )
          : false;
        const country: SelectorCountry = {
          ...c,
          authDisabled: countryAuthDisabled,
        };

        return country;
      });
    }

    return this.authCountries;
  }

  public get countriesMap(): Map<string, SelectorCountry> {
    if (!this.authCountriesMap) {
      this.authCountriesMap = new Map();
      this.countries.forEach((c) =>
        (<Map<string, SelectorCountry>>this.authCountriesMap).set(c.code, c)
      );
    }

    return this.authCountriesMap;
  }

  public get generations(): ToggleableNameValuePair[] {
    if (!this.authGenerations) {
      this.authGenerations =
        this.systemGroupsService.systemGroups.generationGroups.map(
          (generation) => {
            return new ToggleableNameValuePair(generation, generation);
          }
        );
    }

    return this.authGenerations;
  }

  public get ageGroups(): AgeGroup[] {
    if (!this.authAgeGroups) {
      this.authAgeGroups = this.systemGroupsService.systemGroups.ageGroups.map(
        (ageGroup) => {
          const authDisabled = !this.authService.isLoggedIn();
          return new AgeGroup(
            ageGroup.greaterThan,
            ageGroup.lessThan,
            ageGroup.name,
            authDisabled,
            ageGroup.id,
            authDisabled
          );
        }
      );
    }
    return this.authAgeGroups;
  }

  public get genders(): ToggleableNameValuePair[] {
    if (!this.authGenders) {
      this.authGenders = constants.data.genderOptions.map((gender) => {
        const authDisabled = !this.authService.isLoggedIn();
        return {
          ...gender,
          disabled: authDisabled,
          authDisabled: this.permissions.isFreemium || authDisabled,
        };
      });
    }

    return this.authGenders;
  }

  public get spendingGroups(): SpendingGroup[] {
    if (!this.authSpendingGroups) {
      this.authSpendingGroups =
        this.systemGroupsService.systemGroups.spendingGroups.map(
          (spendingGroup) => {
            return new SpendingGroup(
              spendingGroup.name,
              spendingGroup.greaterThan,
              spendingGroup.lessThan,
              spendingGroup.id,
              spendingGroup.subgroups
            );
          }
        );
    }

    return this.authSpendingGroups;
  }

  public get incomeGroups(): IncomeGroup[] {
    if (!this.authIncomeGroups) {
      this.authIncomeGroups =
        this.systemGroupsService.systemGroups.incomeGroups.map(
          (incomeGroup) => {
            return new IncomeGroup(
              incomeGroup.name,
              incomeGroup.greaterThan,
              incomeGroup.lessThan,
              incomeGroup.id,
              undefined,
              false,
              false,
              false
            );
          }
        );
    }

    return this.authIncomeGroups;
  }

  public get startYear(): number {
    if (this.permissions.isFreemium) {
      return constants.freemiumStartYear;
    }
    if (this.hasCategoriesEnabled) {
      return constants.categoriesStartYear;
    }

    return constants.startYear;
  }

  public get endYear(): number {
    return this.permissions.allowedYears.length
      ? Math.max(...this.permissions.allowedYears)
      : constants.endYear;
  }
}
